import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatFileSize } from '../../../../utils';
import FilePreviewViewer from './FilePreviewViewer';
import FileDropZone from './FileDropZone';

interface FileEntry {
  file: File;
  label: string;
}

interface FilePreviewPanelProps {
  primaryFile: File | null;
  additionalFiles: FileEntry[];
  onAddFiles: (files: File[]) => void;
  onRemoveAdditional: (index: number) => void;
  onLabelChange: (index: number, label: string) => void;
}

const FilePreviewPanel: React.FC<FilePreviewPanelProps> = ({
  primaryFile,
  additionalFiles,
  onAddFiles,
  onRemoveAdditional,
  onLabelChange,
}) => {
  const { t } = useTranslation();
  const allFiles = primaryFile
    ? [{ file: primaryFile, label: t('Primary') as string }, ...additionalFiles]
    : [...additionalFiles];
  const [activeTab, setActiveTab] = useState(0);

  if (allFiles.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <FileDropZone
          onFilesSelected={onAddFiles}
          multiple
          className="flex-1"
        />
      </div>
    );
  }

  const activeFile = allFiles[activeTab]?.file;

  return (
    <div className="flex flex-col h-full gap-3">
      {/* File tabs */}
      {allFiles.length > 1 && (
        <div className="flex gap-1 overflow-x-auto rounded-lg bg-muted p-1">
          {allFiles.map((entry, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveTab(i)}
              className={`flex-shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === i
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {entry.file.name.length > 20
                ? entry.file.name.slice(0, 17) + '...'
                : entry.file.name}
            </button>
          ))}
        </div>
      )}

      {/* Preview */}
      {activeFile && <FilePreviewViewer file={activeFile} className="flex-1 min-h-0" />}

      {/* Active file info */}
      {activeFile && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground truncate">{activeFile.name}</span>
          <span>{formatFileSize(activeFile.size)}</span>
          <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs">
            {activeFile.type.startsWith('image/') ? t('Image') : t('PDF')}
          </span>
        </div>
      )}

      {/* Additional files list */}
      {additionalFiles.length > 0 && (
        <div className="space-y-1.5">
          {additionalFiles.map((entry, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
              <input
                type="text"
                value={entry.label}
                onChange={(e) => onLabelChange(i, e.target.value)}
                placeholder={t('Label (e.g. Back)') as string}
                className="w-24 flex-shrink-0 rounded border-none bg-transparent px-0 py-0 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-0"
              />
              <span className="flex-1 truncate text-xs text-muted-foreground">{entry.file.name}</span>
              <span className="text-xs text-muted-foreground">{formatFileSize(entry.file.size)}</span>
              <button
                type="button"
                onClick={() => onRemoveAdditional(i)}
                aria-label={t('Remove file')}
                className="rounded p-0.5 text-muted-foreground hover:text-destructive transition-colors"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add more */}
      <FileDropZone
        onFilesSelected={onAddFiles}
        multiple
        compact
      />
    </div>
  );
};

export default FilePreviewPanel;
